/* ============================================================
   LG PRODUCT BLOCK
   Toggle, pack ladder, gallery, delivery estimate.

   Cart adds are NOT here — Dawn's <product-form> owns them. This file only
   keeps the quantity input in sync with the selected pack before submit.

   Every instance is scoped to its own section element. The Turbo original used
   document.getElementById on global ids, so a second instance on the same page
   silently drove the first one's form.
   ============================================================ */
(function () {
  'use strict';

  function initProductBlock(section) {
    if (section.dataset.lgInit === 'true') return;
    section.dataset.lgInit = 'true';

    /* ---------- Subscribe / one-time tabs ---------- */
    const tabs = section.querySelectorAll('.lg-product__toggle-btn');
    const panels = section.querySelectorAll('.lg-product__panel');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        const target = tab.dataset.panel;
        panels.forEach((p) => {
          p.classList.toggle('is-visible', p.classList.contains('lg-product__panel--' + target));
        });
      });
    });

    /* ?buy=once (the homepage "Buy once" card) opens on the one-time tab, so
       the plan a shopper chose there is the one they land on. A ?selling_plan=
       link needs nothing here: Liquid pre-selects that rung. */
    if (new URLSearchParams(location.search).get('buy') === 'once') {
      const oneTab = section.querySelector('.lg-product__toggle-btn[data-panel="onetime"]');
      if (oneTab) oneTab.click();
    }

    /* Roving arrow keys across the two tabs — expected of role="tablist". */
    tabs.forEach((tab, i) => {
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = tabs[e.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length];
        next.focus();
        next.click();
      });
    });

    /* ---------- Pack ladder ----------
       The selected pack's quantity is written into the one-time form's hidden
       quantity input. This is the whole reason the ladder needs JS. */
    const bundles = section.querySelectorAll('.lg-product__bundle');
    const onetimePanel = section.querySelector('.lg-product__panel--onetime');
    const qtyInput = onetimePanel && onetimePanel.querySelector('.lg-product__qty-input');

    function selectBundle(label) {
      bundles.forEach((b) => b.classList.remove('is-selected'));
      label.classList.add('is-selected');
      const input = label.querySelector('input[type="radio"]');
      if (input) input.checked = true;
      if (qtyInput) qtyInput.value = label.dataset.bundle || '1';
    }

    bundles.forEach((label) => {
      /* Listen on change, not click: the <label> wraps a real radio, so click
         fires twice (once for the label, once for the forwarded input) and
         keyboard selection fires change only. */
      const input = label.querySelector('input[type="radio"]');
      if (input) input.addEventListener('change', () => selectBundle(label));
    });

    /* Sync on load — the pre-selected pack may not be qty 1. */
    const preselected = section.querySelector('.lg-product__bundle.is-selected');
    if (preselected && qtyInput) qtyInput.value = preselected.dataset.bundle || '1';

    /* ---------- Plan ladder ----------
       Only rendered when the product carries more than one selling plan.

       Two inputs move together and BOTH matter. The plan id decides what
       Recharge bills; the quantity decides how much of it. The quarterly plan
       is "3 packs every 3 months" at £40 a pack — post the plan without
       quantity 3 and the shopper pays £40 for one pack while Recharge bills a
       three-pack. Neither side errors, which is why this is written once, here,
       rather than left to whoever edits the template next.

       Scoped to the subscribe panel: the one-time panel has a hidden quantity
       input of its own, driven by the pack ladder above, and writing to the
       wrong one silently changes the other tab's order. */
    const subPanel = section.querySelector('.lg-product__panel--subscribe');
    const plans = section.querySelectorAll('.lg-product__plan');

    if (subPanel && plans.length) {
      const planInput = subPanel.querySelector('.lg-product__plan-input');
      const planQtyInput = subPanel.querySelector('.lg-product__qty-input');
      const priceEl = subPanel.querySelector('.lg-product__price-current');
      const compareEl = subPanel.querySelector('.lg-product__price-compare');
      const freqEl = subPanel.querySelector('.lg-product__sub-freq');
      const deliveryEl = subPanel.querySelector('.lg-product__sub-delivery');
      const saveEl = subPanel.querySelector('.lg-product__sub-save');

      function selectPlan(label) {
        plans.forEach((p) => p.classList.remove('is-selected'));
        label.classList.add('is-selected');

        const radio = label.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;

        if (planInput) planInput.value = label.dataset.plan || planInput.value;
        if (planQtyInput) planQtyInput.value = label.dataset.qty || '1';

        /* The headline price follows the choice. Left alone it keeps the
           hand-typed monthly figure and the card contradicts the row the
           shopper just clicked. */
        if (priceEl && label.dataset.total) priceEl.textContent = label.dataset.total;
        if (compareEl) {
          const c = label.dataset.compare;
          compareEl.textContent = c || '';
          compareEl.hidden = !c;
        }

        /* The frequency and the saving are hand-typed settings describing the
           MONTHLY plan. Left alone, choosing the quarterly showed £120 above
           "/month · cancel anytime" and "SAVE 15%" — a three-monthly price
           wearing a monthly label, which is the same class of mismatch that put
           the £47 card over the £120 plan in the first place. */
        if (freqEl && label.dataset.freq) freqEl.textContent = label.dataset.freq;
        if (deliveryEl && label.dataset.delivery !== undefined) deliveryEl.textContent = label.dataset.delivery;
        if (saveEl) {
          const pct = parseInt(label.dataset.savepct, 10);
          if (pct > 0) {
            saveEl.textContent = 'Save ' + pct + '%';
            saveEl.hidden = false;
          } else {
            saveEl.hidden = true;
          }
        }
      }

      plans.forEach((label) => {
        /* change, not click — the <label> wraps a real radio, so click fires
           twice and keyboard selection fires change only. Same reason as the
           pack ladder above. */
        const radio = label.querySelector('input[type="radio"]');
        if (radio) radio.addEventListener('change', () => selectPlan(label));
      });

      /* Sync on load, so the form agrees with whatever is pre-checked. */
      const preselectedPlan = section.querySelector('.lg-product__plan.is-selected');
      if (preselectedPlan) selectPlan(preselectedPlan);
    }

    /* ---------- Gallery ----------
       The track is a real scroll container, so the swipe needs no code at all.
       This only does three things: move the track when an arrow or thumb is
       clicked, read back which slide the scroll landed on, and keep the thumbs
       and the arrows' disabled state in step with it.

       Scroll position is the single source of truth. Driving the two directions
       from separate state is how these end up disagreeing after a flick that
       lands between slides. */
    const track = section.querySelector('.lg-product__gallery-main');
    const slides = section.querySelectorAll('.lg-product__slide');
    const thumbs = section.querySelectorAll('.lg-product__thumb');
    const thumbStrip = section.querySelector('.lg-product__thumbs');
    const prevBtn = section.querySelector('.lg-product__nav--prev');
    const nextBtn = section.querySelector('.lg-product__nav--next');

    if (track && slides.length) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0;

      /* Measured, not assumed: the slide is 100% of the track today, but a
         future peek-the-next-image setting would break `index * clientWidth`
         silently and only on some widths. */
      function slideStep() {
        return slides.length > 1
          ? slides[1].offsetLeft - slides[0].offsetLeft
          : track.clientWidth;
      }

      function indexFromScroll() {
        const step = slideStep();
        if (!step) return 0;
        return Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
      }

      /* The thumbnails are one scrolling row now, so most of them are off-screen
         most of the time. Without this the ring marking the current image sits
         outside the strip from about image five onward and the row looks stuck.

         Deliberately NOT scrollIntoView: that walks up and scrolls every
         scrollable ancestor, so on a phone it yanks the page as well as the
         strip. Set the strip's own scrollLeft instead. */
      function revealThumb(idx, smooth) {
        if (!thumbStrip) return;
        const t = thumbs[idx];
        if (!t) return;
        const visible = thumbStrip.clientWidth;
        const max = thumbStrip.scrollWidth - visible;
        const centred = t.offsetLeft - (visible - t.offsetWidth) / 2;
        thumbStrip.scrollTo({
          left: Math.max(0, Math.min(max, centred)),
          behavior: smooth ? 'smooth' : 'auto',
        });
      }

      /* The edge fade goes on only where something is actually hidden past it.
         Always-on, the first thumbnail looks damaged before anyone has
         scrolled. 2px of slack because scrollLeft is fractional on zoomed and
         high-DPI displays and never quite reaches its own maximum. */
      function syncThumbEdges() {
        if (!thumbStrip) return;
        const max = thumbStrip.scrollWidth - thumbStrip.clientWidth;
        thumbStrip.dataset.atStart = String(thumbStrip.scrollLeft <= 2);
        thumbStrip.dataset.atEnd = String(max <= 2 || thumbStrip.scrollLeft >= max - 2);
      }

      /* PDP-SPEED 16 Sep 2026 — slides after the first are rendered with
         data-src/data-srcset only (see the Liquid). Hydrate on demand: the
         slide being shown and the one after it, everything on first touch of
         the track, and slide 2 on its own once the page has loaded so the
         first arrow tap still lands on a ready image. */
      function hydrate(idx) {
        const s = slides[idx];
        if (!s) return;
        const img = s.querySelector('img[data-srcset]');
        if (!img) return;
        img.setAttribute('srcset', img.dataset.srcset);
        img.setAttribute('src', img.dataset.src);
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
      }
      function hydrateAll() {
        for (let i = 0; i < slides.length; i += 1) hydrate(i);
      }
      /* Warm slide 2 only once the LCP has settled: 1.5s after the last LCP
         candidate, or 3s after load if the observer never fires. On slow 4G
         `load` can beat the hero image, so load alone is not a safe trigger. */
      let warmTimer = 0;
      const scheduleWarm = (ms) => {
        clearTimeout(warmTimer);
        warmTimer = setTimeout(() => hydrate(1), ms);
      };
      try {
        new PerformanceObserver(() => scheduleWarm(1500)).observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) { /* no LCP support: fall through to load */ }
      const onLoad = () => { if (!warmTimer) scheduleWarm(3000); };
      if (document.readyState === 'complete') onLoad();
      else window.addEventListener('load', onLoad, { once: true });
      track.addEventListener('pointerdown', hydrateAll, { once: true, passive: true });
      track.addEventListener('touchstart', hydrateAll, { once: true, passive: true });

      function paint(idx, smooth) {
        current = idx;
        hydrate(idx);
        hydrate(idx + 1);

        slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));

        thumbs.forEach((t, i) => {
          t.classList.toggle('is-active', i === idx);
          if (i === idx) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });

        revealThumb(idx, smooth);

        if (prevBtn) prevBtn.disabled = idx <= 0;
        if (nextBtn) nextBtn.disabled = idx >= slides.length - 1;
      }

      function goTo(idx) {
        const clamped = Math.min(slides.length - 1, Math.max(0, idx));

        /* Animate a step, cut to a jump. Chrome's smooth scroll is
           distance-proportional: thumbnail 1 to thumbnail 10 measured 1.2s of
           eight images strobing past, which reads as a stutter rather than as
           motion. The thumbnail has already told the shopper where they are
           going, so there is nothing for the animation to explain. Arrows are
           always one step, so they keep the animation.
           `scroll-behavior: smooth` in the CSS is overridden by passing this
           explicitly, which is also why reduced motion is re-checked here. */
        const smooth = !reduceMotion.matches && Math.abs(clamped - current) <= 1;

        track.scrollTo({
          left: clamped * slideStep(),
          behavior: smooth ? 'smooth' : 'auto',
        });
        /* Paint immediately rather than waiting for the scroll to settle — a
           400ms smooth scroll with a stale thumbnail underneath reads as a
           dropped tap. The scroll handler confirms it afterwards. */
        paint(clamped, smooth);
      }

      /* rAF-throttled: scroll fires far more often than the DOM needs touching,
         and this runs during a finger drag. */
      let ticking = false;
      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const idx = indexFromScroll();
            if (idx !== current) paint(idx, !reduceMotion.matches);
          });
        },
        { passive: true }
      );

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => goTo(parseInt(thumb.dataset.index, 10)));
      });

      /* The track is focusable (it scrolls), so it owes keyboard users the same
         two steps the arrows give everyone else. */
      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        goTo(current + (e.key === 'ArrowRight' ? 1 : -1));
      });

      if (thumbStrip) {
        let thumbTicking = false;
        thumbStrip.addEventListener(
          'scroll',
          () => {
            if (thumbTicking) return;
            thumbTicking = true;
            requestAnimationFrame(() => {
              thumbTicking = false;
              syncThumbEdges();
            });
          },
          { passive: true }
        );
      }

      /* Re-measure after a resize: slideStep is width-dependent, and a rotation
         leaves scrollLeft pointing between two slides. The strip's overflow
         changes with width too, so its fades have to be recomputed. */
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          goTo(current);
          syncThumbEdges();
        }, 150);
      });

      paint(0, false);
      syncThumbEdges();

      /* The strip's scrollWidth is wrong until the thumbnails have laid out,
         and they are lazy-loaded. One more pass after load settles the fades. */
      window.addEventListener('load', syncThumbEdges, { once: true });
    }

    /* ---------- Delivery estimate ----------
       Weekday dispatch before noon UK, then Royal Mail 48 = 2 business days. */
    const arrivalEls = section.querySelectorAll('[data-lg-arrival-date]');
    if (arrivalEls.length) {
      const CUTOFF_HOUR = 12;
      const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const MONTHS = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];

      /* Read "now" in Europe/London regardless of the shopper's own timezone —
         a US visitor at 8am local must not be told they made a UK noon cutoff. */
      function ukNow() {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hour12: false,
        }).formatToParts(new Date());
        const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
        return { year: get('year'), month: get('month') - 1, day: get('day'), hour: get('hour') % 24 };
      }

      const isWeekday = (d) => d.getDay() >= 1 && d.getDay() <= 5;

      function addBusinessDays(start, days) {
        const d = new Date(start);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (isWeekday(d)) added += 1;
        }
        return d;
      }

      const uk = ukNow();
      const today = new Date(uk.year, uk.month, uk.day);

      /* Dispatch today only if it is a weekday and still before noon; otherwise
         the next business day. */
      let dispatch = new Date(today);
      if (!isWeekday(today) || uk.hour >= CUTOFF_HOUR) {
        dispatch = addBusinessDays(today, 1);
      }

      const arrival = addBusinessDays(dispatch, 2);
      const text = DAYS[arrival.getDay()] + ' ' + arrival.getDate() + ' ' + MONTHS[arrival.getMonth()];
      arrivalEls.forEach((el) => {
        el.textContent = text;
      });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('.lg-product').forEach(initProductBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
